import { useState } from "react";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { aboutData } from "@/mocks/about";

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const aboutUrl = "https://hanmanonline.com/about";

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于韩漫在线",
    description: "韩漫在线是国内领先的韩国漫画在线阅读平台，致力于为中国读者提供最全面、最新鲜的韩漫阅读体验。",
    url: aboutUrl,
    isPartOf: {
      "@type": "WebSite",
      name: "韩漫在线",
      url: "https://hanmanonline.com",
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: aboutData.faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "韩漫在线",
    url: "https://hanmanonline.com",
    description: "国内领先的韩国漫画在线阅读平台",
    email: aboutData.contact.email,
    address: {
      "@type": "PostalAddress",
      streetAddress: "望京SOHO塔3-B座",
      addressLocality: "北京",
      addressRegion: "朝阳区",
      addressCountry: "CN",
    },
    contactPoint: {
      "@type": "ContactPoint",
      email: aboutData.contact.email,
      contactType: "customer service",
      availableLanguage: ["Chinese"],
    },
  };

  const breadcrumbItems = [
    { label: "首页", href: "/" },
    { label: "关于我们" },
  ];

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <div className="pt-20">
        <Breadcrumb items={breadcrumbItems} />

        <section className="w-full px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-3xl mx-auto text-center mb-14">
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mb-4">
                关于<strong>韩漫在线</strong>
              </h1>
              <p className="text-base text-foreground-500 leading-relaxed">
                {aboutData.tagline}
              </p>
            </div>

            <div className="max-w-3xl mx-auto mb-16">
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                {aboutData.introduction}
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed">
                {aboutData.mission}
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-20">
              {aboutData.stats.map((stat, i) => (
                <div key={i} className="text-center p-6 bg-background-100 rounded-lg">
                  <div className="font-heading text-3xl md:text-4xl font-bold text-primary-500 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
              {aboutData.features.map((feature, i) => (
                <div key={i} className="p-6 bg-background-50 border border-background-200/70 rounded-lg text-center">
                  <div className="w-12 h-12 mx-auto mb-4 bg-accent-100 rounded-full flex items-center justify-center">
                    <i className={`${feature.icon} text-accent-600 text-xl w-5 h-5 flex items-center justify-center`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                用户口碑
              </h2>
              <p className="text-sm text-foreground-500">听听我们的用户怎么说</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {aboutData.reviews.map((review, i) => (
                <div key={i} className="p-6 bg-background-50 rounded-lg border border-background-200/70">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200/70 flex-shrink-0">
                      <img
                        src={review.avatar}
                        alt={review.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">{review.name}</div>
                      <div className="flex items-center gap-0.5">
                        {Array.from({ length: 5 }).map((_, s) => (
                          <i
                            key={s}
                            className={`w-3 h-3 flex items-center justify-center ${
                              s < review.rating
                                ? "ri-star-fill text-accent-500"
                                : "ri-star-line text-foreground-300"
                            }`}
                          ></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">{review.comment}</p>
                  <p className="text-xs text-foreground-400">{review.date}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                常见问题
              </h2>
              <p className="text-sm text-foreground-500">关于韩漫在线的常见疑问解答</p>
            </div>
            <div className="max-w-3xl mx-auto space-y-3">
              {aboutData.faqs.map((faq, i) => (
                <div
                  key={i}
                  className="border border-background-200/70 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-background-100/50 transition-colors"
                  >
                    <span className="text-sm font-medium text-foreground-900 pr-4">
                      {faq.question}
                    </span>
                    <i
                      className={`w-5 h-5 flex items-center justify-center text-foreground-400 transition-transform duration-200 flex-shrink-0 ${
                        openFaq === i ? "ri-subtract-line" : "ri-add-line"
                      }`}
                    ></i>
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500">如有任何问题，欢迎通过以下方式联系我们</p>
            </div>
            <div className="max-w-2xl mx-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-4 bg-background-50 rounded-lg border border-background-200/70">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <div className="text-xs text-foreground-400 mb-0.5">电子邮箱</div>
                    <div className="text-sm font-medium text-foreground-900">{aboutData.contact.email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-background-50 rounded-lg border border-background-200/70">
                  <div className="w-10 h-10 bg-accent-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-wechat-line text-accent-600 w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <div className="text-xs text-foreground-400 mb-0.5">微信公众号</div>
                    <div className="text-sm font-medium text-foreground-900">{aboutData.contact.wechat}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-background-50 rounded-lg border border-background-200/70">
                  <div className="w-10 h-10 bg-secondary-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-weibo-line text-secondary-600 w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <div className="text-xs text-foreground-400 mb-0.5">微博</div>
                    <div className="text-sm font-medium text-foreground-900">{aboutData.contact.weibo}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-background-50 rounded-lg border border-background-200/70">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <div className="text-xs text-foreground-400 mb-0.5">办公地址</div>
                    <div className="text-sm font-medium text-foreground-900">{aboutData.contact.address}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}